/**
 * Where each technology sits on the tree screen.
 *
 * Pure arithmetic over the content, so it is testable and computed once. Two rules:
 *
 *   COLUMNS ARE BANDED BY ERA. Each era gets a run of columns of its own, left to right
 *   in era order, and within the band a technology's column is its depth in the tree.
 *   Because a technology never needs one from a later era (content validation enforces
 *   that), every prerequisite lands strictly to the left of what it leads to — so every
 *   line on the screen runs left to right, and the eras read as chapters.
 *
 *   ROWS FOLLOW PARENTAGE. Each technology is placed as near as it can get to the
 *   average row of its prerequisites, so lines stay short and a strand of the tree
 *   (seafaring, say, or metalwork) tends to run along one level of the screen.
 */

import type { Content } from '../../content/schema/index.js';

export interface TechPlacement {
  col: number;
  row: number;
}

export interface EraBand {
  era: number;
  firstCol: number;
  lastCol: number;
}

export interface TechLayout {
  /** Indexed by technology id. */
  place: TechPlacement[];
  columns: number;
  rows: number;
  bands: EraBand[];
}

export function layoutTechTree(content: Content): TechLayout {
  const techs = content.technologies.all;
  const eraCount = content.eras.all.length;

  const minDepth = new Array<number>(eraCount).fill(Infinity);
  const maxDepth = new Array<number>(eraCount).fill(-Infinity);
  for (const t of techs) {
    minDepth[t.eraIndex] = Math.min(minDepth[t.eraIndex]!, t.depth);
    maxDepth[t.eraIndex] = Math.max(maxDepth[t.eraIndex]!, t.depth);
  }

  const bands: EraBand[] = [];
  let next = 0;
  for (let era = 0; era < eraCount; era++) {
    if (!Number.isFinite(minDepth[era]!)) continue; // an era with no technologies yet
    const width = maxDepth[era]! - minDepth[era]! + 1;
    bands.push({ era, firstCol: next, lastCol: next + width - 1 });
    next += width;
  }
  const bandStart = new Map(bands.map((b) => [b.era, b.firstCol]));

  const place: TechPlacement[] = techs.map((t) => ({
    col: bandStart.get(t.eraIndex)! + t.depth - minDepth[t.eraIndex]!,
    row: 0,
  }));

  // Fill columns left to right, so every technology's prerequisites are already placed.
  const byColumn = new Map<number, number[]>();
  techs.forEach((t) => {
    const col = place[t.id]!.col;
    byColumn.set(col, [...(byColumn.get(col) ?? []), t.id]);
  });

  let rows = 0;
  for (let col = 0; col < next; col++) {
    const ids = byColumn.get(col) ?? [];
    const wanted = new Map<number, number>();
    for (const id of ids) {
      const parents = techs[id]!.prerequisites;
      // Roots have no parents; they keep file order, which is how the author grouped them.
      wanted.set(id, parents.length === 0 ? ids.indexOf(id) : parents.reduce((sum, p) => sum + place[p]!.row, 0) / parents.length);
    }
    ids.sort((a, b) => wanted.get(a)! - wanted.get(b)! || a - b);

    let floor = 0;
    for (const id of ids) {
      const row = Math.max(floor, Math.round(wanted.get(id)!));
      place[id]!.row = row;
      floor = row + 1;
      rows = Math.max(rows, row + 1);
    }
  }

  return { place, columns: next, rows, bands };
}
